import { parseLocalDateString } from './parseLocalDateString';

/**
 * Calculates the age of an appliance in whole calendar months, matching the
 * behaviour of PostgreSQL's `AGE()` function combined with `EXTRACT(month ...)`.
 *
 * This replicates the server-side SQL logic:
 * ```sql
 * EXTRACT(month FROM AGE(NOW(), DATE(i.item_purchase_dts)))
 * ```
 *
 * ## How PostgreSQL AGE() handles months
 *
 * Postgres performs a field-by-field subtraction (year, month, day) and then
 * propagates negative fields upward. When the day difference is negative
 * (i.e. the current day-of-month is less than the purchase day-of-month),
 * it borrows one month and adds back the number of days in the purchase
 * date's month. `EXTRACT(month ...)` then discards the remaining days,
 * returning only the whole-month count.
 *
 * For example, a purchase on Dec 30 evaluated on Feb 28:
 * - Day diff: 28 − 30 = −2 → borrow 1 month, add December's 31 days → 29 days
 * - Month diff: 2 − 12 = −10 − 1 (borrowed) = −11 → borrow 1 year → 1 month
 * - Result: `1 mon 29 days` → EXTRACT(month) = 1
 *
 * The simplified JS check `today.getDate() < purchase.getDate()` captures this:
 * if we haven't reached the purchase day-of-month yet, the current month isn't
 * complete, so we subtract one from the calendar month difference.
 *
 * @see {@link https://github.com/postgres/postgres/blob/master/src/backend/utils/adt/timestamp.c | timestamp_age() source}
 * @see {@link https://domgen.atlassian.net/wiki/spaces/DOE/pages/9832104042/POST+pricing#SQL-for-ProductVariant-lookup-%26-validation | Internal docs for product age calculation}
 *
 * @param purchaseDate - ISO 8601 date string (YYYY-MM-DD)
 * @returns Whole calendar months since purchase, matching Postgres `EXTRACT(month FROM AGE(...))`
 */

export const calculateAgeInMonths = (purchaseDate: string): number => {
  const today = new Date();
  const purchase = parseLocalDateString(purchaseDate);

  let months =
    (today.getFullYear() - purchase.getFullYear()) * 12 + (today.getMonth() - purchase.getMonth());

  if (today.getDate() < purchase.getDate()) {
    months--;
  }

  return months;
};
